import { useState, useEffect } from 'react'
import clsx from 'clsx'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { cuttingApi, procurementApi } from '../../../shared/lib/api'
import { useAuthStore } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import { StepBar, InfoCard, FlowNote, BigButton, QRBox, SuccessScreen, Divider } from '../../../shared/components/UI'

const STEPS = ['Select Roll', 'No. of Rolls', 'Weight']

export default function CuttingPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [workOrder, setWorkOrder] = useState(location.state?.workOrder || null)

  const [step, setStep] = useState(0)
  const [loadingRolls, setLoadingRolls] = useState(true)
  const [availableRolls, setAvailableRolls] = useState([])
  const [selectedRoll, setSelectedRoll] = useState(workOrder?.roll || null)

  const [partialRolls, setPartialRolls] = useState([])
  const [mergeMode, setMergeMode] = useState(false)
  const [mergeRoll, setMergeRoll] = useState(null)
  const [numRolls, setNumRolls] = useState('')
  const [kgPerRoll, setKgPerRoll] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    // If we have a workOrder but it's not fully loaded with a roll
    if (workOrder && !selectedRoll) {
      setSelectedRoll(workOrder.roll)
    }

    // Fetch rolls available to cut (status = purchased or partial)
    setLoadingRolls(true)
    Promise.all([
      procurementApi.listRolls({ status: 'purchased', limit: 20 }),
      procurementApi.listRolls({ status: 'partial', limit: 20 })
    ]).then(([purchasedRes, partialRes]) => {
      const combined = [...(purchasedRes.data.data || []), ...(partialRes.data.data || [])]
      setAvailableRolls(combined)
      setPartialRolls(partialRes.data.data || [])
    }).finally(() => setLoadingRolls(false))
  }, [workOrder])

  const totalCut = numRolls && kgPerRoll ? (parseFloat(numRolls) * parseFloat(kgPerRoll)).toFixed(2) : null

  const { mutate, isPending } = useMutation({
    mutationFn: () => cuttingApi.submit({
      workOrderId: workOrder?.id || null,
      rollId: selectedRoll?.id,
      mergedRollId: mergeRoll?.id,
      smallRollsCount: parseInt(numRolls),
      weightPerRollKg: parseFloat(kgPerRoll),
      totalCutWeight: parseFloat(totalCut),
    }),
    onSuccess: () => setStep(3),
    onError: (err) => setError(err.response?.data?.message || 'Submission failed. Please try again.'),
  })

  const handleNext = () => {
    setError('')
    if (step === 0 && !selectedRoll) return setError('Please select a roll to cut')
    if (step === 1) {
      if (!numRolls || parseInt(numRolls) < 1) return setError('Enter number of small rolls')
    }
    if (step === 2) {
      if (!kgPerRoll || parseFloat(kgPerRoll) <= 0) return setError('Enter weight of one roll')
      mutate()
      return
    }
    setStep(s => s + 1)
  }

  // No redirect anymore, we handle selection if missing
  const currentRoll = selectedRoll || {}

  return (
    <TabletScreen>
      <TabletHeader title="Cutting" role="Metallic Master" unit={user?.unitName || 'Unit'} />
      <div className="p-4">
        {step < 3 && <StepBar steps={STEPS} current={step} />}

        {/* Step 0 — Select Roll */}
        {step === 0 && (
          <>
            <p className="section-heading">Select a roll to cut</p>
            <div className="flex flex-col gap-3 mb-5 max-h-[40vh] overflow-y-auto pr-1">
              {loadingRolls && <div className="py-8 text-center text-gray-400">Loading rolls...</div>}
              {!loadingRolls && availableRolls.length === 0 && <div className="py-8 text-center text-gray-400 border border-dashed border-gray-200 rounded-2xl">No available rolls found</div>}
              {availableRolls.map(r => (
                <div key={r.id} onClick={() => setSelectedRoll(r)}
                  className={clsx('select-card', selectedRoll?.id === r.id && 'select-card-active')}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-sm text-gray-800 font-mono">{r.id.substring(0, 8)}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${r.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{r.status}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{r.shade?.name || 'Unknown Shade'}</span>
                    <span className="font-medium text-gray-700">{r.net_weight_kg} KG</span>
                  </div>
                </div>
              ))}
            </div>

            {selectedRoll && (
              <div className="animate-in fade-in slide-in-from-bottom-2">
                <p className="section-heading mt-6">Selected Roll Details</p>
                <InfoCard rows={[
                  { label: 'Batch No.', value: currentRoll.batch_no || 'Internal' },
                  { label: 'Shade', value: currentRoll.shade?.name || 'Unknown' },
                  { label: 'Net weight', value: `${currentRoll.net_weight_kg || 0} KG` },
                ]} />
                
                {/* Merge remaining roll option */}
                <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-4 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Merge remaining roll?</p>
                      <p className="text-xs text-gray-500 mt-0.5">Combine with a previously partial roll</p>
                    </div>
                    <button
                      onClick={() => setMergeMode(m => !m)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${mergeMode ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200'}`}
                    >
                      {mergeMode ? 'Merging' : 'No merge'}
                    </button>
                  </div>
                  {mergeMode && (
                    <div className="space-y-2">
                      {partialRolls.length === 0 && <p className="text-xs text-gray-400 py-2">No partial rolls found</p>}
                      {partialRolls.map(r => (
                        <div key={r.id} onClick={() => setMergeRoll(prev => prev?.id === r.id ? null : r)}
                          className={clsx('select-card', mergeRoll?.id === r.id && 'select-card-active')}>
                          <div className="flex justify-between">
                            <div>
                              <p className="text-xs font-semibold font-mono text-gray-800">{r.id.substring(0, 8)}</p>
                              <p className="text-xs text-gray-500">{r.shade?.name}</p>
                            </div>
                            <p className="text-sm font-semibold text-amber-600">{r.net_weight_kg} KG remaining</p>
                          </div>
                        </div>
                      ))}
                      {mergeRoll && (
                        <div className="bg-teal-50 rounded-lg px-3 py-2 text-xs text-teal-700">
                          Will merge: {mergeRoll.id.substring(0, 8)} ({mergeRoll.net_weight_kg} KG) + current roll ({currentRoll.net_weight_kg} KG) = {(parseFloat(mergeRoll.net_weight_kg) + parseFloat(currentRoll.net_weight_kg)).toFixed(2)} KG total
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            <BigButton variant="success" onClick={handleNext} disabled={!selectedRoll}>Start Cutting</BigButton>
            <BigButton variant="secondary" onClick={() => navigate('/app/dashboard')}>Back</BigButton>
          </>
        )}

        {/* Step 1 — No. of rolls */}
        {step === 1 && (
          <>
            <FlowNote>Cutting: {currentRoll.id?.substring(0, 8)} · {currentRoll.shade?.name} · {currentRoll.net_weight_kg} KG</FlowNote>
            <div className="mb-4">
              <label className="form-label text-sm mb-2 block">How many small rolls did you cut?</label>
              <input
                type="number"
                inputMode="numeric"
                className="form-input text-4xl text-center py-5 font-semibold"
                placeholder="0"
                min="1"
                value={numRolls}
                onChange={(e) => setNumRolls(e.target.value)}
                autoFocus
              />
            </div>
            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            <BigButton variant="success" onClick={handleNext}>Cutting Done → Enter Weight</BigButton>
            <BigButton variant="secondary" onClick={() => setStep(0)}>Back</BigButton>
          </>
        )}

        {/* Step 2 — Weight */}
        {step === 2 && (
          <>
            <InfoCard rows={[{ label: 'Small rolls cut', value: `${numRolls} rolls`, valueVariant: 'blue' }]} />
            <div className="mb-4">
              <label className="form-label text-sm mb-2 block">Weigh one roll — enter its weight (KG)</label>
              <input
                type="number"
                inputMode="decimal"
                className="form-input text-4xl text-center py-5 font-semibold"
                placeholder="0.0"
                min="0.1"
                step="0.1"
                value={kgPerRoll}
                onChange={(e) => setKgPerRoll(e.target.value)}
                autoFocus
              />
            </div>
            {totalCut && (
              <div className="info-card" style={{ background: '#EBF4FF', borderColor: '#9DD0F5' }}>
                <div className="info-row" style={{ borderBottom: 'none' }}>
                  <span className="info-label">Total cut weight</span>
                  <span className="info-value info-value-blue">{totalCut} KG</span>
                </div>
              </div>
            )}
            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            <BigButton variant="success" onClick={handleNext} disabled={isPending}>
              {isPending ? 'Saving…' : 'Confirm & Submit'}
            </BigButton>
            <BigButton variant="secondary" onClick={() => setStep(1)}>Back</BigButton>
          </>
        )}

        {/* Done */}
        {step === 3 && (
          <>
            <SuccessScreen
              title={`${numRolls} small rolls created`}
              subtitle="QR codes generated for each roll. Continue to Bobbin & Batch to create containers."
            />
            <div className="flex gap-4 justify-center mb-6">
              <QRBox label="SR-1" size={90} />
              {parseInt(numRolls) > 1 && <QRBox label="SR-2" size={90} />}
              {parseInt(numRolls) > 2 && (
                <div className="opacity-40">
                  <QRBox label={`+${parseInt(numRolls) - 2} more`} size={90} />
                </div>
              )}
            </div>
            <Divider />
            <BigButton variant="success" onClick={() => navigate('/app/bobbin')}>
              Continue to Bobbin & Batch
            </BigButton>
            <BigButton variant="primary" onClick={() => navigate('/app/dashboard')}>Back to Dashboard</BigButton>
            <BigButton variant="secondary" onClick={() => { setStep(0); setNumRolls(''); setKgPerRoll('') }}>
              Cut another roll
            </BigButton>
          </>
        )}
      </div>
    </TabletScreen>
  )
}
